import React from 'react'
import { InvestorForm, InvestorFormWithId, validateFn } from './AdminInvestorsFields'
import { FormProvider, useForm } from 'react-hook-form'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import { FormTable, FormTableRow } from '../../issues/FormTable'
import { RegisteredTextField } from '../../../../components/fields/TextField'
import { RegisteredNumericField } from '../../../../components/fields/NumericField'
import { RegisteredCheckBox } from '../../../../components/fields/CheckBox'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'

interface InvestorDialogProps {
    investor: InvestorFormWithId | null
    isFeeLevelEnabled: boolean
    isLeoEnabled: boolean
    isWithoutRightsEnabled: boolean
    isPercentageEnabled: boolean
    isSellerEnabled: boolean
    handleSubmitInvestor: (investor: InvestorForm) => void
    open: boolean
    setOpen: (val: boolean) => void
    validateName: validateFn
    validateAmount: validateFn
    validateFeeLevel: validateFn
    validateLeo: validateFn
}

export function InvestorDialog({
    investor,
    isFeeLevelEnabled,
    isLeoEnabled,
    isWithoutRightsEnabled,
    isPercentageEnabled,
    isSellerEnabled,
    handleSubmitInvestor,
    open,
    setOpen,
    validateName,
    validateAmount,
    validateFeeLevel,
    validateLeo,
}: InvestorDialogProps) {
    const formMethods = useForm<InvestorForm>({
        defaultValues: investor
            ? {
                  ...investor,
                  withoutRights: !!(investor.withoutRights === true || investor.withoutRights === 1),
              }
            : {
                  name: '',
                  amountInPercentage: null,
                  amountInCash: null,
                  feeLevel: null,
                  isSeller: null,
                  leo: null,
                  withoutRights: false,
              },
    })
    const handleClose = () => {
        setOpen(false)
    }
    const handleSubmit = () => {
        const data = formMethods.getValues()
        const fee = data.feeLevel
        const newInvestor = {
            ...investor,
            ...data,
            withoutRights: isWithoutRightsEnabled && fee !== null && Number(fee) === 0 ? !!data.withoutRights : false,
        }
        handleSubmitInvestor(newInvestor)
        setOpen(false)
    }

    const isFormValid = formMethods.formState.isValid && validateLeo('', formMethods.getValues())

    return (
        <FormProvider {...formMethods}>
            <Dialog
                key={(investor?.tempId ?? '') + (investor?.id ?? '') + (investor?.name ?? '')}
                open={open}
                onClose={() => setOpen(false)}
            >
                <DialogTitle>Investerare</DialogTitle>
                <DialogContent>
                    <FormTable>
                        <FormTableRow label='Namn'>
                            <RegisteredTextField label='Namn' name='name' validation={{ validate: validateName }} />
                        </FormTableRow>
                        {isPercentageEnabled && (
                            <FormTableRow label='Procent'>
                                <RegisteredNumericField
                                    label='Procent'
                                    name='amountInPercentage'
                                    percentage={true}
                                    disabled={!!formMethods.watch('isSeller')}
                                    validation={{ validate: validateAmount }}
                                    onChange={() => void formMethods.trigger('amountInCash')}
                                />
                            </FormTableRow>
                        )}
                        <FormTableRow label='Belopp'>
                            <RegisteredNumericField
                                label='Belopp'
                                name='amountInCash'
                                decimals={0}
                                disabled={!!formMethods.watch('isSeller')}
                                validation={{ validate: validateAmount }}
                                onChange={() => void formMethods.trigger('amountInPercentage')}
                            />
                        </FormTableRow>
                        {isFeeLevelEnabled && (
                            <FormTableRow label='Nivå'>
                                <RegisteredNumericField
                                    label='Nivå'
                                    name='feeLevel'
                                    decimals={0}
                                    disabled={!!formMethods.watch('isSeller')}
                                    validation={{ validate: validateFeeLevel }}
                                    onChangeValue={(v) => {
                                        const n = v === '' ? null : +v
                                        if (n !== null && n !== 0) {
                                            formMethods.setValue('withoutRights', false)
                                        }
                                    }}
                                />
                            </FormTableRow>
                        )}
                        {isLeoEnabled && (
                            <FormTableRow label=''>
                                <RegisteredCheckBox label='Leo' name='leo' disabled={!!formMethods.watch('isSeller')} />
                            </FormTableRow>
                        )}
                        {isWithoutRightsEnabled && isFeeLevelEnabled && (
                            <FormTableRow label=''>
                                <RegisteredCheckBox
                                    label='TÅ utan rätter'
                                    name='withoutRights'
                                    disabled={
                                        !!formMethods.watch('isSeller') ||
                                        formMethods.watch('feeLevel') === null ||
                                        Number(formMethods.watch('feeLevel')) !== 0
                                    }
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            formMethods.setValue('feeLevel', 0)
                                        }
                                        void formMethods.trigger()
                                    }}
                                />
                            </FormTableRow>
                        )}
                        {isSellerEnabled && (
                            <FormTableRow label=''>
                                <RegisteredCheckBox
                                    label='Säljare av befintliga aktier'
                                    name='isSeller'
                                    onChange={() => {
                                        formMethods.setValue('amountInCash', null)
                                        formMethods.setValue('amountInPercentage', null)
                                        formMethods.setValue('feeLevel', null)
                                        formMethods.setValue('leo', null)
                                        formMethods.setValue('withoutRights', false)
                                        void formMethods.trigger()
                                    }}
                                />
                            </FormTableRow>
                        )}
                    </FormTable>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Avbryt</Button>
                    <Button disabled={!isFormValid} onClick={handleSubmit}>
                        Spara
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}
